<script lang="ts">
import type { Component, PropType, VNodeChild } from 'vue';
import { computed, defineComponent, h } from 'vue';

// Stable wrapper — prevents remounting when the render function reference changes.
// <component :is="fn"> treats `fn` as a component identity: new reference = unmount + remount.
// This wrapper has a fixed identity; changing `renderFn` just re-invokes it, patching VNodes in place.
const RenderFnWrapper = defineComponent({
	inheritAttrs: false,
	props: {
		renderFn: { type: [Function, Object] as PropType<Component>, required: true },
	},
	setup(props, { attrs }) {
		return () => h(props.renderFn, attrs);
	},
});
</script>

<script lang="ts" setup generic="TValues extends object = Record<string, unknown>, TName extends string = string">
import type { FormSchemaFieldActual, FormSchemaFieldValue } from '#layers/block-engine/types/form-schema';
import type { FormSchemaAdapter } from '#layers/block-engine/utils/form-schema';
import { createReusableTemplate } from '@vueuse/core';
import { provideFieldNullable } from '#layers/block-engine/composables/use-field-nullable';
import { blockExpressionMarker } from '#layers/block-engine/utils/block/expression';
import { useTranslationUtils } from '#layers/i18n';
import { injectFormContext } from '#layers/ui';

const props = defineProps<{
	field: FormSchemaFieldActual<TValues, TName>;
	adapter: FormSchemaAdapter<TValues>;
}>();

const emit = defineEmits<{
	setValue: [value: FormSchemaFieldValue<TValues, TName>];
}>();

const { translateString } = useTranslationUtils();

// Let input blocks derive a clear affordance from the field's nullability.
provideFieldNullable(() => props.field.nullable ?? true);

const model = computed<FormSchemaFieldValue<TValues, TName>>({
	get: () => {
		// Defaults only apply after the active adapter source reports an unset value.
		const value = props.adapter.get({
			field: props.field,
		});
		// Missing values fall back to schema defaults; an explicit null is preserved
		// (the user cleared the field) unless the field opts into defaultOnNull.
		const useDefault = value === undefined || (value === null && props.field.defaultOnNull);
		return (useDefault ? props.field.default?.() : value) as FormSchemaFieldValue<TValues, TName>;
	},
	set: (value) => {
		emit('setValue', value);
	},
});

/**
 * Update the local model from custom render slots.
 */
function updateModel(value: FormSchemaFieldValue<TValues, TName>) {
	model.value = value;
}

// Render

const render = computed(() => {
	if ('render' in props.field && props.field.render) {
		return props.field.render;
	}

	return false;
});

// Block slots

const fieldSlots = computed<Record<string, (slotProps: Record<string, unknown>) => VNodeChild>>(() => {
	if ('slots' in props.field && props.field.slots) {
		return props.field.slots as Record<string, (slotProps: Record<string, unknown>) => VNodeChild>;
	}

	return {};
});

// Special handling for InputSwitch block

const formContext = injectFormContext();

const isStackedLayout = computed(() => formContext.layout.value === 'stacked' || props.field.fieldProps?.layout === 'stacked');

const isExpression = computed(() => {
	const value = model.value;
	return typeof value === 'string' && value.startsWith(blockExpressionMarker);
});

const isInputSwitch = computed(() => props.field.block as unknown as string === '$builtin:InputSwitch');

const moveSwitchToLabel = computed(() => isStackedLayout.value && !isExpression.value && isInputSwitch.value && !render.value);

// Reusable template

const [DefineRenderContent, RenderContent] = createReusableTemplate();
</script>

<template>
	<DefineRenderContent>
		<slot
			v-if="render"
			name="render"
			:render
			:value="model"
			:set-value="updateModel"
		>
			<RenderFnWrapper
				v-model="model"
				:render-fn="render"
				class="w-full"
			/>
		</slot>

		<BlockRender
			v-else-if="!moveSwitchToLabel && field.block"
			v-model="model"
			:block="field.block"
			:props="field.props"
		>
			<template
				v-for="(slot, name) in fieldSlots"
				:key="name"
				#[name]="slotProps"
			>
				<component :is="slot(slotProps)" />
			</template>
		</BlockRender>
	</DefineRenderContent>

	<RenderContent
		v-if="field.unwrapField"
		v-bind="$attrs"
	/>
	<DFormField
		v-else
		v-bind="{
			...$attrs,
			...field.fieldProps,
		}"
		:data-field-name="field.name"
		:name="field.name"
		:description="translateString(field.description)"
		:required="field.required"
		:ui="{
			label: 'flex w-full items-center',
		}"
		class="group/form-schema-field"
		:class="{
			'[&_label+div]:hidden': moveSwitchToLabel,
		}"
	>
		<template #label>
			<div
				v-if="moveSwitchToLabel"
			>
				<DSwitch
					v-model="model"
					class="mr-2"
				/>
			</div>

			<DIconOrImage
				v-if="field.icon"
				:icon="field.icon"
			/>

			<DFormFieldLabel :label="translateString(field.label)" :required="field.required" />

			<!-- Prevent switch toggle -->
			<div
				class="flex flex-1 items-center"
				@click.prevent
			>
				<slot name="hint" />
			</div>
		</template>

		<slot>
			<RenderContent />
		</slot>
	</DFormField>
</template>
